import Drawer from "@via/components/drawer";
import { DrawerSize } from "@via/components/table/table";
import LoginDrawerForm from "@via/pages/projects/metadata/configuration/data-import/components/login-drawer-form";
import { getErrorResponseMessage } from "@via/utils/helpers.tsx";
import type { VmetaAdminImportLogin } from "common";
import { type JSX, useState } from "react";
import { groupByMergeKey, useAdminImportLogins } from "./import-logins.api.tsx";
import MergeModal from "./merge-modal";

const ImportLogins = (): JSX.Element => {
  const { data, isLoading, isError, error } = useAdminImportLogins();
  const [editing, setEditing] = useState<VmetaAdminImportLogin | null>(null);
  const [moving, setMoving] = useState<VmetaAdminImportLogin[] | null>(null);
  const groups = groupByMergeKey(data?.items ?? []);

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-gray-700">
        Every metadata import sign in, personal ones included. Rows grouped
        together share a platform, login address, API address and username, so
        they can be moved into one organization sign in. Passwords and keys are
        never shown. Every change made here is recorded in the audit log.
      </p>
      {isLoading ? (
        <p>Loading…</p>
      ) : isError ? (
        <div role="alert" className="text-sm text-red-700">
          <p>
            The sign in list could not be loaded. Reload the page to try again.
          </p>
          {getErrorResponseMessage(error) && (
            <p>{getErrorResponseMessage(error)}</p>
          )}
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left">
              <th>Name</th>
              <th>Platform</th>
              <th>Belongs to</th>
              <th>Owner</th>
              <th>API address</th>
              <th>Key expires</th>
              <th>Used by</th>
              <th />
            </tr>
          </thead>
          {groups.map(group => (
            <tbody key={group[0].mergeKey} className="border-t">
              {group.map((row, index) => (
                <tr key={row.id}>
                  <td>
                    <button
                      type="button"
                      className="text-blue-600"
                      onClick={() => setEditing(row)}>
                      {row.name}
                    </button>
                  </td>
                  <td>{row.platform}</td>
                  <td>
                    {row.scope === "organization"
                      ? (row.organizationName ?? "organization")
                      : "Personal"}
                  </td>
                  <td>{row.ownerEmail ?? "unknown"}</td>
                  <td>{row.apiOrigin}</td>
                  <td>
                    {row.keyExpiresAt
                      ? new Date(row.keyExpiresAt).toLocaleDateString()
                      : ""}
                  </td>
                  <td>{row.usedBy}</td>
                  <td>
                    {index === 0 &&
                      (group.length > 1 ||
                        group.some(r => r.scope === "personal")) && (
                        <button
                          type="button"
                          className="text-blue-600"
                          onClick={() => setMoving(group)}>
                          Move to organization
                        </button>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      )}
      <Drawer
        id="admin-import-login-editor"
        open={!!editing}
        setOpen={() => setEditing(null)}
        size={DrawerSize.Big}
        title="Edit credential">
        {editing ? (
          <LoginDrawerForm
            login={editing}
            platform={editing.platform}
            organizations={[]}
            onClose={() => setEditing(null)}
          />
        ) : undefined}
      </Drawer>
      <MergeModal
        key={moving?.[0].mergeKey ?? "closed"}
        group={moving}
        organizations={data?.organizations ?? []}
        onClose={() => setMoving(null)}
      />
    </div>
  );
};

export default ImportLogins;
